import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import {
  Comment,
  CommentHide,
  CommentQuery,
  Engagement,
  EngagementQuery,
  HideCommentMutation,
} from '../types';

const { REACT_APP_GRAPHQL_APIURL } = process.env;

const queryEngagements = `query Engagement($teamAbbrv: String!) {
  engagement(abbrv: $teamAbbrv) {
    contentId
    contentType
    contentTitle
    numberOfReactions
    lastEngagementAt
  }
}`;

const queryComments = `query Comments($teamAbbrv: String!) {
  comments(abbrv: $teamAbbrv) {
    id
    contentId
    contentType
    contentTitle
    fullName
    text
    createdAt
    isHidden
  }
}`;

const mutateHideComment = `mutation CommentHide($id: Int!, $hide: Boolean!) {
  commentHide(id: $id, hide: $hide) {
    id
    isHidden
  }
}`;

export const useSocial = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();

  const getEngagements = useCallback(
    async (teamAbbrv: string): Promise<Engagement[] | null> => {
      let engagementResult: Engagement[] | null = null;

      try {
        const response = await fetch(REACT_APP_GRAPHQL_APIURL as string, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: queryEngagements,
            variables: { teamAbbrv },
          }),
        });

        if (!response.ok) {
          throw new Error(`Get engagement failed! (${response.status})`);
        }

        const data = await response.json();

        if (data.errors) {
          console.error(data.errors);
          throw new Error('Query engagement failed!');
        }

        engagementResult = (data?.data as EngagementQuery)?.engagement || [];
      } catch (error: any) {
        console.error(error);
        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }

      return engagementResult;
    },
    [enqueueSnackbar, strings]
  );

  const getComments = useCallback(
    async (teamAbbrv: string): Promise<Comment[] | null> => {
      let commentsResult: Comment[] | null = null;

      try {
        const response = await fetch(REACT_APP_GRAPHQL_APIURL as string, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: queryComments,
            variables: { teamAbbrv },
          }),
        });

        if (!response.ok) {
          throw new Error(`Get comments failed! (${response.status})`);
        }

        const data = await response.json();

        if (data.errors) {
          console.error(data.errors);
          throw new Error('Query comments failed!');
        }

        commentsResult = (data?.data as CommentQuery)?.comments || [];
      } catch (error: any) {
        console.error(error);
        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }

      return commentsResult;
    },
    [enqueueSnackbar, strings]
  );

  const hideComment = useCallback(
    async (id: number, hide: boolean) => {
      let commentHideResult: CommentHide | null = null;

      try {
        const response = await fetch(REACT_APP_GRAPHQL_APIURL as string, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: mutateHideComment,
            variables: { id, hide },
          }),
        });

        if (!response.ok) {
          throw new Error(`Hide comment failed! (${response.status})`);
        }
        const data = await response.json();

        if (data.errors) {
          console.error(data.errors);
          throw new Error('Mutate comment failed!');
        }

        commentHideResult = (data?.data as HideCommentMutation)?.commentHide || null;
      } catch (error: any) {
        console.error(error);
        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }

      return commentHideResult;
    },
    [enqueueSnackbar, strings]
  );

  return {
    getEngagements,
    getComments,
    hideComment,
  };
};
